import React, { useEffect, useState } from 'react';
import { Table, theme } from 'antd';
import { useSelector } from 'react-redux';
import { RootState } from '../../store/index';
import { CheckOutlined, CloseOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { apiGetPage } from '../../request/api'
import { useCustomData } from '../../pages/context/serviceCustomDataProvider';

interface Service {
    serviceRange: string;
    description: string;
    schemeOne: boolean;
    schemeTwo: boolean;
}

interface GroupedService extends Service {
    rowSpan?: number;
}
interface ServiceDetailTableProps {
    handleViewChange: (newView: string, data?: any) => void;
    servicesInfo: any;
    onOpenModal?: (type: 'basic' | 'premium') => void;
    onOpenNote?: (type: any) => void;
}

const ServiceDetailTable: React.FC<ServiceDetailTableProps> = ({ handleViewChange, servicesInfo, onOpenModal, onOpenNote }) => {
    const themeColor = useSelector((state: RootState) => state.themeColor.value);
    const languageCode = localStorage.getItem('siteLanguage') || 'zh';
    const [tableData, setTableData] = useState<any[]>([]);
    const [packageList, setPackageList] = useState<any[]>([]);
    const { getValue } = useCustomData();
    const remarks = getValue('remarks');
    const iwant = getValue('iwant');
    const describe = getValue('describe');
    const serviceRange = getValue('serviceRange');
    const [planTitles, setPlanTitles] = useState<{ planOne: string; planTwo: string }>({
        planOne: '方案一',
        planTwo: '方案二',
    });
    useEffect(() => {
        if (!servicesInfo) return;
        const [planOne, planTwo] = servicesInfo.split(',');

        // 请求服务详情，组装表格数据
        apiGetPage({
            tid: '0B5BEE67FE7B419C8C33CFD33A11CA5F',
            page: 1,
            size: 100,
            languagecode: languageCode,
            search: 'belongToCommodity:' + planOne + '/belongToCommodity:' + planTwo,
        }).then(res => {
            const rows = res.data.data.rows || [];
            const merged = new Map<string, Service>();

            rows.forEach((item: any) => {
                const key = item.serviceScope + '|' + item.serviceDescription;
                const isPlanOne = item.belongToCommodity === planOne;
                const isPlanTwo = item.belongToCommodity === planTwo;

                if (!merged.has(key)) {
                    merged.set(key, {
                        serviceRange: item.serviceScope,
                        description: item.serviceDescription,
                        schemeOne: isPlanOne,
                        schemeTwo: isPlanTwo,
                    });
                } else {
                    const exist = merged.get(key)!;
                    if (isPlanOne) exist.schemeOne = true;
                    if (isPlanTwo) exist.schemeTwo = true;
                }
            });

            const mergedArr = Array.from(merged.values());
            const processed = processData(mergedArr);
            setTableData(processed);
        });

        // 请求套餐信息，更新列头
        apiGetPage({
            tid: '72D99CCB9A7E439184BF9EF7CABC49BA',
            page: 1,
            size: 100,
            languagecode: languageCode,
            search: 'id:' + planOne + '/id:' + planTwo,
        }).then(res => {
            const rows = res.data.data.rows || [];
            setPackageList(rows);   // ✅ 保存下来
            if (rows.length >= 2) {
                setPlanTitles({
                    planOne: rows[0].priceRemark,
                    planTwo: rows[1].priceRemark,
                });
            }
        });
    }, [servicesInfo, languageCode]);


    const processData = (data: Service[]): GroupedService[] => {
        const groups = new Map<string, Service[]>();

        data.forEach(item => {
            const key = item.serviceRange;
            groups.has(key) ? groups.get(key)?.push(item) : groups.set(key, [item]);
        });

        const processedData: GroupedService[] = [];
        groups.forEach((groupItems, key) => {
            groupItems.forEach((item, index) => {
                processedData.push({
                    ...item,
                    rowSpan: index === 0 ? groupItems.length : 0
                });
            });
        });
        return processedData;
    };


    // 自定义样式
    const tableStyle = {
        '--theme-color': themeColor,
        '--table-bg': 'rgba(14,8,249,0.05)',
        '--text-color': '#33364b',
        '--header-height': '97px'
    } as React.CSSProperties;

    // 表格列定义
    const columns = [
        {
            title: serviceRange,
            width: 180,
            dataIndex: 'serviceRange',
            render: (value: string, record: GroupedService) => ({
                children: <span className="service-range-cell">{value}</span>,
                props: { rowSpan: record.rowSpan }
            }),
        },
        {
            title: describe,
            width: 640,
            dataIndex: 'description',
            render: (text: string) => <span className="description-cell">{text}</span>
        },
        {
            title: planTitles.planOne,
            width: 190,
            render: (_: any, record: Service) => (
                record.schemeOne
                    ? <CheckOutlined style={{ color: themeColor }} />
                    : <CloseOutlined style={{ color: '#33364b' }} />
            ),
        },
        {
            title: planTitles.planTwo,
            width: 190,
            render: (_: any, record: Service) => (
                record.schemeTwo
                    ? <CheckOutlined style={{ color: themeColor }} />
                    : <CloseOutlined style={{ color: '#33364b' }} />
            ),
        },
    ];

    const footer = () => (
        <div className="table-footer" style={{ backgroundColor: '#f2f4fe' }}>
            <div className='table-note'>
                <div>{remarks}</div>
                <InfoCircleOutlined
                    onClick={() => {
                        console.log(packageList[0])
                        const List = {
                            commodityType: packageList[0].commodityType + "-" + packageList[0].commodityOtherName,
                            commodityRemark: packageList[0].commodityRemark
                        }
                        onOpenNote?.(List);
                    }}
                    style={{ fontSize: '20px', color: themeColor }}
                />
            </div>
            <div className='service-table_bottom'>
                <button
                    onClick={() => {
                        const basicPlan = packageList.find(item => item.id === servicesInfo.split(',')[0]);
                        if (basicPlan) onOpenModal?.(basicPlan);
                    }}
                    style={{ marginRight: '35px', background: themeColor, boxShadow: `0 2px 4px 0 ` + themeColor, lineHeight: '17px' }}
                >
                    {iwant}
                </button>
                <button
                    onClick={() => {
                        const premiumPlan = packageList.find(item => item.id === servicesInfo.split(',')[1]);
                        if (premiumPlan) onOpenModal?.(premiumPlan);
                    }}
                    style={{ background: themeColor, boxShadow: `0 2px 4px 0 ` + themeColor, lineHeight: '17px' }}
                >
                    {iwant}
                </button>
            </div>
        </div>
    );

    return (
        <div className="serviceDetailTable" style={tableStyle}>
            <Table
                columns={columns}
                dataSource={tableData}
                bordered
                pagination={false}
                footer={footer}
                rowKey="description"
            />
        </div>
    );
};

export default ServiceDetailTable;